import { useMemo } from 'react';

import { usePatientEligibilitiesQuery } from '@/entities/eligibility';
import { usePatientPriorAuthsQuery, type PriorAuthSummaryT } from '@/entities/prior-auth';
import { usePatientPrescriptionsQuery } from '@/entities/rx';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { statusToneClassName } from '@/shared/ui/statusTone';
import { cn } from '@/lib/utils';

import {
  describePriorAuth,
  formatMedicationName,
  groupPriorAuth,
  sortPriorAuths,
  type PriorAuthGroup,
} from '../model/priorAuthPresentation';

export interface PriorAuthsTableProps {
  userId: string;
  onSelect: (summary: PriorAuthSummaryT) => void;
}

const GROUPS: { id: PriorAuthGroup; title: string; headingClassName: string }[] = [
  { id: 'needs-you', title: 'Needs your attention', headingClassName: 'text-amber-500' },
  { id: 'waiting', title: 'Waiting for insurance', headingClassName: 'text-foreground/70' },
  { id: 'settled', title: 'History', headingClassName: 'text-foreground/70' },
];

function formatDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function PriorAuthsTable({ userId, onSelect }: PriorAuthsTableProps) {
  const query = usePatientPriorAuthsQuery(userId);
  const prescriptionsQuery = usePatientPrescriptionsQuery(userId);
  const eligibilitiesQuery = usePatientEligibilitiesQuery(userId);
  const prescriptionById = useMemo(
    () =>
      new Map(
        (prescriptionsQuery.data?.prescriptions ?? []).map((prescription) => [
          String(prescription.PrescriptionId),
          prescription,
        ])
      ),
    [prescriptionsQuery.data]
  );
  const eligibilityById = useMemo(
    () =>
      new Map(
        (eligibilitiesQuery.data?.items ?? []).flatMap((eligibility) =>
          eligibility.PatientEligibilityId == null
            ? []
            : [[String(eligibility.PatientEligibilityId), eligibility] as const]
        )
      ),
    [eligibilitiesQuery.data]
  );
  const grouped = useMemo(() => {
    const result = new Map<PriorAuthGroup, PriorAuthSummaryT[]>(GROUPS.map(({ id }) => [id, []]));
    for (const summary of query.data ?? []) {
      result.get(groupPriorAuth(summary))?.push(summary);
    }
    for (const { id } of GROUPS) {
      result.set(id, sortPriorAuths(id, result.get(id) ?? []));
    }
    return result;
  }, [query.data]);

  if (query.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading prior authorizations…</div>;
  }
  if (query.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load prior authorizations</AlertTitle>
        <AlertDescription>
          {query.error instanceof Error ? query.error.message : 'An unknown error occurred.'}
        </AlertDescription>
      </Alert>
    );
  }
  if (!query.data?.length) {
    return <div className="text-sm text-muted-foreground">No prior authorizations yet.</div>;
  }

  return (
    <div className="space-y-7">
      {prescriptionsQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Medication names unavailable</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>Prior authorizations are still shown below without medication names.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void prescriptionsQuery.refetch()}
              disabled={prescriptionsQuery.isFetching}
            >
              {prescriptionsQuery.isFetching ? 'Trying again…' : 'Try again'}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      {GROUPS.map(({ id, title, headingClassName }) => {
        const rows = grouped.get(id) ?? [];
        if (!rows.length) {
          return null;
        }
        return (
          <section key={id} aria-labelledby={`pa-${id}`} className="space-y-2">
            <h3
              id={`pa-${id}`}
              className={cn('text-xs font-semibold uppercase tracking-wide', headingClassName)}
            >
              {title} <span className="ml-1 font-normal">{rows.length}</span>
            </h3>
            <div className="space-y-2">
              {rows.map((summary) => {
                const copy = describePriorAuth(summary);
                const prescription = prescriptionById.get(summary.dosespotPrescriptionId);
                const medication = formatMedicationName(
                  prescription?.DisplayName,
                  prescription?.Strength
                );
                const eligibility =
                  prescription?.EligibilityId == null
                    ? undefined
                    : eligibilityById.get(String(prescription.EligibilityId));
                const insurance = eligibility?.PayerName ?? eligibility?.PlanName;
                const updated = formatDate(summary.currentStatusDate ?? summary.lastSeenAt);
                const deadline = formatDate(summary.nextResponseDeadline);
                const metadata = [
                  id === 'needs-you' && deadline ? `Respond by ${deadline}` : null,
                  id === 'waiting' && deadline ? `Expected update by ${deadline}` : null,
                  id === 'settled' && updated ? `Updated ${updated}` : null,
                  insurance,
                ].filter(Boolean);
                return (
                  <button
                    key={summary.id}
                    type="button"
                    onClick={() => onSelect(summary)}
                    className={cn(
                      'flex w-full items-center gap-4 rounded-xl border px-4 py-3.5 text-left transition-colors',
                      id === 'needs-you'
                        ? 'border-amber-500/30 bg-amber-500/[0.06] hover:bg-amber-500/[0.1]'
                        : id === 'settled'
                          ? 'border-border/70 bg-card/70 hover:bg-accent/30'
                          : 'border-border bg-card hover:bg-accent/40'
                    )}
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-[15px] font-semibold">{medication}</span>
                        <Badge className={statusToneClassName(copy.tone)}>{copy.label}</Badge>
                      </div>
                      <p className="text-sm leading-relaxed text-foreground/80">
                        {copy.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {id === 'waiting' ? 'No action needed' : null}
                        {metadata.length > 0
                          ? `${id === 'waiting' ? ' · ' : ''}${metadata.join(' · ')}`
                          : null}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'rounded-full px-4 py-2 text-xs font-semibold',
                        id === 'needs-you'
                          ? 'bg-foreground text-background'
                          : 'bg-muted text-foreground'
                      )}
                    >
                      {copy.actionLabel}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
